import type { DataProvider } from '@refinedev/core';
import { apiGet, apiPatch, apiPut, ApiError } from './http';

/**
 * Data provider do Refine sobre a NOSSA API `/admin/*`. Não é o simple-rest
 * genérico porque a nossa lista devolve `{ data, total, limit, offset }` e o
 * filtro de texto é `?search=` — mapeia-se aqui, num só sítio.
 *
 * Só implementa o que a consola usa (getList/getOne/update/custom). O resto
 * lança — melhor um erro claro do que um CRUD meio-funcional a fingir.
 */
// A consola fala SEMPRE com o plano `/admin/*` (RBAC platform.*). Antes chamava `/users` e `/events` — os
// endpoints PÚBLICOS: lia o diretório (hoje sem campos privados → «inativo» em todos) e o PATCH de
// ativar/desativar apontava a uma rota que não existe.
const admin = (resource: string) => `/admin/${resource}`;

const naoSuportado = (op: string) => { throw new ApiError(501, `dataProvider.${op} não implementado`); };

export const dataProvider: DataProvider = {
  getApiUrl: () => '/admin',

  async getList({ resource, pagination, filters }) {
    const current = pagination?.current ?? 1;
    const pageSize = pagination?.pageSize ?? 50;
    const q = new URLSearchParams();
    q.set('limit', String(pageSize));
    q.set('offset', String((current - 1) * pageSize));
    // Só o filtro de pesquisa livre (o que a API `/admin/*` aceita).
    const search = filters?.find((f) => 'field' in f && f.field === 'search');
    if (search && 'value' in search && search.value) q.set('search', String(search.value));
    const res = await apiGet<{ data: unknown[]; total: number }>(`${admin(resource)}?${q.toString()}`);
    return { data: (res.data ?? []) as never[], total: res.total ?? 0 };
  },

  async getOne({ resource, id }) {
    // A API não tem GET /admin/<res>/:id — resolvemos a partir da lista (a
    // consola trabalha sobre listas; o detalhe é raro). Best-effort.
    const res = await apiGet<{ data: Array<{ id: string }> }>(`${admin(resource)}?limit=200`);
    const item = (res.data ?? []).find((r) => String(r.id) === String(id));
    return { data: (item ?? { id }) as never };
  },

  async update({ resource, id, variables }) {
    const data = await (resource === 'events' ? apiPut : apiPatch)<Record<string, unknown>>(`${admin(resource)}/${id}`, variables);
    return { data: data as never };
  },

  create: () => naoSuportado('create'),
  deleteOne: () => naoSuportado('deleteOne'),

  async custom({ url, method, payload }) {
    const data = method === 'patch'
      ? await apiPatch<unknown>(url, payload)
      : await apiGet<unknown>(url);
    return { data: data as never };
  },
};
